/**
 * `GET preferences/user`, as `businessStore.userData` holds it, turned into facts. Pure. The ONLY
 * place the record's shape is written down (Q11), and with the store source the only code that
 * reads `allowedFeatures`, `permittedModules`, `assignedLicence`, `featureData`,
 * `accessPreferences` or the role (audit check O).
 */
import { LICENCES, ROLES } from "../vocabulary/backend";
import type { Grant, LicenceCode, MerchantConfig, Role, SubjectFacts, TenantFacts } from "../types";

/** the fields of `userData` the capability model reads (inventory-oldfe-backend.md §5) */
export interface UserRecord {
  allowedFeatures?: unknown;
  permittedModules?: unknown;
  assignedLicence?: unknown;
  role?: unknown;
  accessPreferences?: { discountPercentLimit?: unknown } | null;
  featureData?: unknown;
}

const strings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : []);
const numberOrNull = (value: unknown): number | null => {
  const n = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

/** `<CODE>__<h>_HOURS`, `<CODE>__<h>_HOURS_RESTRICT_DAILY` and `<CODE>__<days>` (user_service.js:2069-2100) */
const SYNTHETIC = /^(.+?)__(\d+)(_HOURS(_RESTRICT_DAILY)?)?$/;

/**
 * `allowedFeatures` → grants. A synthetic code only sets the window of its base code, which the
 * backend always sends beside it; on its own it grants nothing (fail closed).
 */
export function parseGrants(allowedFeatures: unknown): ReadonlyMap<string, Grant> {
  const codes = strings(allowedFeatures);
  const grants = new Map<string, Grant>();
  for (const code of codes) if (!SYNTHETIC.test(code)) grants.set(code, { code });
  for (const code of codes) {
    const [, base, n, hours, daily] = SYNTHETIC.exec(code) ?? [];
    const grant = base ? grants.get(base) : undefined;
    if (!grant) continue;
    grant.window = hours ? { hours: Number(n), ...(daily ? { restrictDaily: true } : {}) } : { days: Number(n) };
  }
  return grants;
}

/** the value when it is one of the known ones, else null */
function oneOf<T extends string>(known: readonly T[], value: unknown): T | null {
  return known.includes(value as T) ? (value as T) : null;
}

export function subjectFrom(user: UserRecord | null | undefined): SubjectFacts {
  return {
    /** the record is in: `GET preferences/user` always carries the modules, even an empty list */
    known: Array.isArray(user?.permittedModules),
    permissions: parseGrants(user?.allowedFeatures),
    modules: new Set(strings(user?.permittedModules)),
    licence: oneOf<LicenceCode>(LICENCES, user?.assignedLicence),
    role: oneOf<Role>(ROLES, user?.role),
    limits: { discountPercentLimit: numberOrNull(user?.accessPreferences?.discountPercentLimit) },
  };
}

interface FeatureDataEntry {
  code?: unknown;
  isEnabled?: unknown;
  value?: unknown;
}

/** `featureData`: `[{ code, isEnabled, value }]`, the access profile's valued features */
export function configFrom(user: UserRecord | null | undefined): TenantFacts {
  const entries: FeatureDataEntry[] = Array.isArray(user?.featureData) ? user.featureData : [];
  const valueOf = (code: string) => {
    const entry = entries.find((e) => e?.code === code && e.isEnabled !== false);
    return entry ? numberOrNull(entry.value) : null;
  };
  const config: MerchantConfig = { reprintWindowHours: valueOf("REPRINT_POST_SETTLEMENT") };
  return { config };
}
